import { FC } from 'react';
import { TransactionDetailInfo } from './transactionDetail.types';
import { useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';
import { Box, formattedCurrency, Flag, GlobalSendRecentTransaction, Card, withStyles, TransactionType } from 'vopay';
import { TransactionCard, Title, DetailsRows, UtilButton } from 'UI';
import ApprovalHistoryDetailsCard from './Card/ApprovalHistoryDetailsCard';
import styles from '../Transactions.module.scss';

const cn = withStyles(styles);

type TransactionInfoProps = {
  transaction: GlobalSendRecentTransaction;
  notes: string;
  jwt: string;
  transactionDetail: TransactionDetailInfo;
};

const CrossBorderWithdrawalInfo: FC<TransactionInfoProps> = ({
  transaction,
  notes,
  jwt,
  transactionDetail,
}: TransactionInfoProps) => {
  const {
    response: { clientAccountsEnabled, JWT: mainJwt },
  } = useClientData();

  const isMainAccount = !jwt || jwt === mainJwt;
  const { accountId } = transactionDetail;

  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.CrossBorderWithdrawalInfo',
  });

  const transactionDetails = [
    {
      label: t('TransactionDetails.type', { defaultValue: 'Transaction Type' }),
      value: t('crossBorderWithdrawal'),
    },
    {
      label: t('TransactionDetails.transactionTypeCode', { defaultValue: 'Transaction Type Code' }),
      value: transaction.TransactionTypeCode,
    },
    {
      label: t('TransactionDetails.transactionDate', { defaultValue: 'Transaction Date' }),
      value: transaction.TransactionDate,
    },
    {
      label: t('TransactionDetails.totalAmount', { defaultValue: 'Total Amount' }),
      value: `${transaction.SenderCurrency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('TransactionDetails.principalAmount', { defaultValue: 'Principal Amount' }),
      value: `${transaction.SenderCurrency} ${formattedCurrency(transaction.PrincipalAmount)}`,
    },
    {
      label: t('TransactionDetails.fees', { defaultValue: 'Fee' }),
      value: `${transaction.SenderCurrency} ${formattedCurrency(
        String(Number(transaction.Amount) - Number(transaction.PrincipalAmount)),
      )}`,
    },
    {
      label: t('TransactionDetails.customerCreditedAmount', { defaultValue: 'Customer Credited Amount' }),
      value: `${transaction.RecipientCurrency} ${formattedCurrency(transaction.CustomerCreditedAmount)}`,
    },
    {
      label: t('TransactionDetails.fxRate', { defaultValue: 'FX Rate' }),
      value: transaction.FXRate,
    },
    {
      label: t('TransactionDetails.destinationCountry', { defaultValue: 'Destination Country' }),
      value: (
        <Box flx="row" ai="center" jc="fe" gapSize="5px">
          {transaction.DestinationCountryAbbreviation}{' '}
          <Flag flag={transaction.DestinationCountryAbbreviation} width="24px" />
        </Box>
      ),
    },
    {
      label: t('TransactionDetails.deliveryMethod', { defaultValue: 'Delivery Method' }),
      value: transaction.DeliveryMethodType,
    },
    {
      label: t('TransactionDetails.glCode', { defaultValue: 'GL Code' }),
      value: transaction.GLCode || '-',
    },
    {
      label: t('TransactionDetails.paymentRailID', { defaultValue: 'Payment Rail ID' }),
      value: transaction.PaymentRailID || '-',
    },
    {
      label: t('TransactionDetails.notes', { defaultValue: 'Notes' }),
      value: notes || '-',
    },
  ];

  const recipientDetails = [
    {
      label: t('RecipientDetails.name', { defaultValue: 'Name' }),
      value: isMainAccount ? (
        <UtilButton
          onClick={() => {
            if (clientAccountsEnabled) {
              window.open(`/client-accounts/${accountId}`, '_blank');
            } else {
              window.open(`/contacts/${accountId}`, '_blank');
            }
          }}
        >
          {transaction.RecipientFirstName + ' ' + transaction.RecipientLastName}
        </UtilButton>
      ) : (
        transaction.RecipientFirstName + ' ' + transaction.RecipientLastName
      ),
    },
    {
      label: t('RecipientDetails.emailAddress', { defaultValue: 'Email Address' }),
      value: transaction.RecipientEmailAddress || '-',
    },
    {
      label: t('RecipientDetails.address', { defaultValue: 'Address' }),
      value: transaction.RecipientAddress1 || transaction.RecipientAddress2 || '-',
    },
    {
      label: t('RecipientDetails.city', { defaultValue: 'City' }),
      value: transaction.RecipientAddressCity,
    },
    {
      label: t('RecipientDetails.postalCode', { defaultValue: 'PostalCode / Zipcode' }),
      value: transaction.RecipientAddressPostalCode,
    },
    {
      label: t('RecipientDetails.country', { defaultValue: 'Country' }),
      value: transaction.RecipientAddressCountry,
    },
    {
      label: t('RecipientDetails.primaryContact', { defaultValue: 'Primary Contact' }),
      value: transaction.RecipientPhone,
    },
    {
      label: t('RecipientDetails.businessName', { defaultValue: 'Business Name' }),
      value: transaction.RecipientBusinessName || '-',
    },
    {
      label: t('RecipientDetails.currency', { defaultValue: 'Currency' }),
      value: transaction.RecipientCurrency,
    },
  ];

  const senderDetails = [
    {
      label: t('SenderDetails.name', { defaultValue: 'Name' }),
      value: (
        <>
          {accountId && accountId !== '' && isMainAccount ? (
            <UtilButton
              onClick={() => {
                if (clientAccountsEnabled) {
                  window.open(`/client-accounts/${accountId}`, '_blank');
                } else {
                  window.open(`/contacts/${accountId}`, '_blank');
                }
              }}
            >
              {transaction.SenderFirstName + ' ' + transaction.SenderLastName}
            </UtilButton>
          ) : (
            <>{transaction.SenderFirstName + transaction.SenderLastName}</>
          )}
        </>
      ),
      visible: !!transaction.SenderFirstName,
    },
    {
      label: t('SenderDetails.emailAddress', { defaultValue: 'EmailAddress' }),
      value: transaction.SenderEmailAddress,
    },
    {
      label: t('SenderDetails.address', { defaultValue: 'Address' }),
      value: [transaction.SenderAddress1, transaction.SenderAddress2, transaction.SenderAddressProvince]
        .filter((item) => !!item)
        .join(', '),
    },
    {
      label: t('SenderDetails.city', { defaultValue: 'City' }),
      value: transaction.SenderAddressCity,
    },
    {
      label: t('SenderDetails.postalCode', { defaultValue: 'PostalCode / Zipcode' }),
      value: transaction.SenderAddressPostalCode,
    },
    {
      label: t('SenderDetails.country', { defaultValue: 'Country' }),
      value: transaction.SenderAddressCountry,
    },
    {
      label: t('SenderDetails.purposeOfPayment', { defaultValue: 'Purpose of Payment' }),
      value: transaction.SenderPurposeOfPayment,
    },
    {
      label: t('SenderDetails.businessName', { defaultValue: 'Business Name' }),
      value: transaction.SenderBusinessName || '-',
    },
    {
      label: t('SenderDetails.currency', { defaultValue: 'Currency' }),
      value: transaction.SenderCurrency,
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.TransactionDate,
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `${transaction.SenderCurrency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: transaction.FailureReason || '-',
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      <TransactionCard
        receiverName={transaction.FirstName + ' ' + transaction.LastName}
        amount={transaction.Amount}
        currency={transaction.SenderCurrency}
        transactionId={transaction.TransactionID}
        type={'Crossborder Withdrawal'}
        failureReason={transaction.FailureReason}
        contactId={accountId}
        jwt={jwt}
        isMainAccount={isMainAccount}
      />

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsTitle', { defaultValue: 'Transaction Details' })}
        </Title>

        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      <ApprovalHistoryDetailsCard
        transactionID={transaction.TransactionID}
        status={transaction.Status}
        type={TransactionType.CROSSBORDER_WITHDRAWAL}
        jwt={jwt}
      />

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('senderDetailsTitle', { defaultValue: 'Sender Details' })}
        </Title>

        <DetailsRows className={cn('rows')} details={senderDetails} />
      </Card>

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('recipientDetailsTitle', { defaultValue: 'Recipient Information' })}
        </Title>

        <DetailsRows className={cn('rows')} details={recipientDetails} />
      </Card>

      {transaction.FailureReason && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoTitle', { defaultValue: 'Error Info' })}
          </Title>

          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default CrossBorderWithdrawalInfo;
